import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, Plus, RefreshCw, Phone, Mail, Building2,
  ArrowUpDown, Filter, MoreHorizontal, Trash2, Edit, Eye,
  TrendingUp, ChevronDown, X, Tag, Upload, FileSpreadsheet, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { getLeads, createLead, updateLead, deleteLead, bulkDeleteLeads, syncLeads, rescoreLead, getCrmAnalytics, importLeadsCSV, exportLeadsCSV } from '../lib/crm-api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-slate-500', text: 'text-slate-400' },
  contacted: { label: 'Contacted', color: 'bg-blue-500', text: 'text-blue-400' },
  interested: { label: 'Interested', color: 'bg-amber-500', text: 'text-amber-400' },
  qualified: { label: 'Qualified', color: 'bg-emerald-500', text: 'text-emerald-400' },
  deal: { label: 'Deal', color: 'bg-primary', text: 'text-primary' },
};

function ScoreBadge({ score }) {
  const tier = score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'cold';
  const styles = {
    hot: 'bg-red-500/15 text-red-400 border-red-500/30',
    warm: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    cold: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  };
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono border', styles[tier])} data-testid="lead-score-badge">
      <TrendingUp className="w-3 h-3" />{score}
    </span>
  );
}

function LeadFormDialog({ open, onOpenChange, lead, onSave }) {
  const [form, setForm] = useState({ phone_number: '', customer_name: '', email: '', company: '', source: 'whatsapp', tags: '', notes: '' });
  const isEdit = !!lead;

  useEffect(() => {
    if (lead) {
      setForm({
        phone_number: lead.phone_number || '',
        customer_name: lead.customer_name || '',
        email: lead.email || '',
        company: lead.company || '',
        source: lead.source || 'whatsapp',
        tags: (lead.tags || []).join(', '),
        notes: lead.notes || '',
      });
    } else {
      setForm({ phone_number: '', customer_name: '', email: '', company: '', source: 'whatsapp', tags: '', notes: '' });
    }
  }, [lead, open]);

  const handleSubmit = () => {
    if (!form.phone_number.trim()) {
      toast.error('Phone number is required');
      return;
    }
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Phone Number *</Label>
            <Input data-testid="lead-phone-input" placeholder="919876543210" value={form.phone_number} onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))} disabled={isEdit} />
          </div>
          <div>
            <Label>Name</Label>
            <Input data-testid="lead-name-input" placeholder="Customer name" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input data-testid="lead-email-input" placeholder="email@example.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <Label>Company</Label>
              <Input data-testid="lead-company-input" placeholder="Company" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Source</Label>
            <Select value={form.source} onValueChange={v => setForm(f => ({ ...f, source: v }))}>
              <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="social">Social Media</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tags (comma separated)</Label>
            <Input data-testid="lead-tags-input" placeholder="vip, mt5-user" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="lead-notes-input" placeholder="Notes about this lead..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="gold-glow" onClick={handleSubmit} data-testid="lead-form-submit">{isEdit ? 'Update' : 'Create'} Lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CRMPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const csvInputRef = useRef(null);

  const allSelected = leads.length > 0 && selectedIds.size === leads.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < leads.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Delete ${count} selected lead${count > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteLeads({ lead_ids: Array.from(selectedIds) });
      toast.success(`Deleted ${res.data.count} leads`);
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteByStage = async (stage) => {
    const stageLabel = STAGE_CONFIG[stage]?.label || stage;
    if (!window.confirm(`Delete ALL leads in "${stageLabel}" stage? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteLeads({ stage });
      toast.success(`Deleted ${res.data.count} leads from "${stageLabel}"`);
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      toast.error('Stage delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (stageFilter !== 'all') params.stage = stageFilter;
      const [leadsRes, analyticsRes] = await Promise.all([getLeads(params), getCrmAnalytics()]);
      setLeads(leadsRes.data);
      setAnalytics(analyticsRes.data);
      setSelectedIds(new Set());
    } catch (err) {
      // Production: error logged silently
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await syncLeads();
      toast.success(`Synced: ${res.data.created} new leads, ${res.data.updated} updated`);
      loadData();
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveLead = async (data) => {
    try {
      if (editingLead) {
        await updateLead(editingLead.id, data);
        toast.success('Lead updated');
      } else {
        await createLead(data);
        toast.success('Lead created');
      }
      setDialogOpen(false);
      setEditingLead(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lead');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLead(id);
      toast.success('Lead deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  const handleRescore = async (id) => {
    try {
      await rescoreLead(id);
      toast.success('Score recalculated');
      loadData();
    } catch (err) {
      toast.error('Failed to rescore');
    }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setCsvImporting(true);
    try {
      const res = await importLeadsCSV(file);
      const { created, skipped, errors, total_rows } = res.data;
      toast.success(`Imported ${created} leads from ${total_rows} rows (${skipped} duplicates skipped)`);
      if (errors?.length > 0) {
        toast.warning(`${errors.length} rows had errors`);
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      setCsvImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const handleCSVExport = async () => {
    try {
      const res = await exportLeadsCSV();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'crm_leads_export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CRM data exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  };

  const stats = [
    { label: 'Total Leads', value: analytics?.total_leads || 0, color: 'text-primary' },
    { label: 'Hot Leads', value: analytics?.score_distribution?.hot || 0, color: 'text-red-400' },
    { label: 'Warm Leads', value: analytics?.score_distribution?.warm || 0, color: 'text-amber-400' },
    { label: 'Avg Score', value: analytics?.avg_score || 0, color: 'text-emerald-400' },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">CRM</h1>
          <p className="text-muted-foreground mt-1">Manage leads and customer relationships</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleSync} disabled={syncing} data-testid="sync-leads-btn">
            <RefreshCw className={cn("w-4 h-4 mr-2", syncing && "animate-spin")} />{syncing ? 'Syncing...' : 'Sync Conversations'}
          </Button>
          <input
            type="file"
            accept=".csv"
            ref={csvInputRef}
            className="hidden"
            onChange={handleCSVImport}
            data-testid="csv-file-input"
          />
          <Button variant="secondary" onClick={() => csvInputRef.current?.click()} disabled={csvImporting} data-testid="import-csv-btn">
            {csvImporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {csvImporting ? 'Importing...' : 'Import CSV'}
          </Button>
          <Button variant="secondary" onClick={handleCSVExport} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          <Button className="gold-glow" onClick={() => { setEditingLead(null); setDialogOpen(true); }} data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-2" />Add Lead
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {stats.map(s => (
          <Card key={s.label} className="bg-card/50 border-border/60">
            <CardContent className="p-4 text-center">
              <p className={cn("text-2xl font-bold", s.color)}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="crm-search-input" className="pl-9" placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearch('')}><X className="w-3.5 h-3.5 text-muted-foreground" /></button>}
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[180px]" data-testid="crm-stage-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {Object.entries(STAGE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" onClick={() => navigate('/pipeline')} data-testid="view-pipeline-btn">
          <TooltipProvider><Tooltip><TooltipTrigger asChild><Eye className="w-4 h-4" /></TooltipTrigger><TooltipContent>Pipeline View</TooltipContent></Tooltip></TooltipProvider>
        </Button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-primary/10 border border-primary/20" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />{bulkDeleting ? 'Deleting...' : 'Delete Selected'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" data-testid="delete-by-stage-btn">
                <Filter className="w-3.5 h-3.5 mr-1.5" />Delete by Stage <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-card border-border">
              {Object.entries(STAGE_CONFIG).map(([key, cfg]) => (
                <DropdownMenuItem key={key} className="text-red-400" onClick={() => handleDeleteByStage(key)}>
                  <Trash2 className="w-3.5 h-3.5 mr-2" />Delete all "{cfg.label}"
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
            <X className="w-3.5 h-3.5 mr-1" />Clear
          </Button>
        </div>
      )}

      {/* Leads Table */}
      <Card className="bg-card/50 border-border/60">
        <ScrollArea className="h-[calc(100vh-380px)]">
          <div className="min-w-[700px]">
            {/* Header */}
            <div className="grid grid-cols-[40px_2fr_1.2fr_1fr_0.8fr_0.8fr_0.5fr] gap-3 px-4 py-3 border-b border-border/60 text-xs text-muted-foreground uppercase tracking-wider font-medium sticky top-0 bg-card/90 backdrop-blur-sm z-10">
              <span className="flex items-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                  data-testid="select-all-checkbox"
                />
              </span>
              <span>Lead</span>
              <span>Last Message</span>
              <span>Stage</span>
              <span>Score</span>
              <span>Last Active</span>
              <span></span>
            </div>

            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : leads.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">No leads found</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Click "Sync Conversations" to import from WhatsApp chats</p>
              </div>
            ) : (
              leads.map(lead => (
                <div key={lead.id} className={cn("grid grid-cols-[40px_2fr_1.2fr_1fr_0.8fr_0.8fr_0.5fr] gap-3 px-4 py-3 border-b border-border/30 hover:bg-muted/20 transition-colors items-center", selectedIds.has(lead.id) && "bg-primary/5")} data-testid={`lead-row-${lead.id}`}>
                  {/* Checkbox */}
                  <span className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={() => toggleSelect(lead.id)}
                      className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                      data-testid={`lead-checkbox-${lead.id}`}
                    />
                  </span>
                  {/* Lead info */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">{(lead.customer_name || lead.phone_number)?.[0]?.toUpperCase() || '?'}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{lead.customer_name || 'Unknown'}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Phone className="w-3 h-3" /><span className="truncate">{lead.phone_number}</span>
                        {lead.email && <><Mail className="w-3 h-3 ml-1" /><span className="truncate">{lead.email}</span></>}
                      </div>
                      {lead.tags?.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">{lead.tags.slice(0, 3).map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground">{t}</span>)}</div>
                      )}
                    </div>
                  </div>

                  {/* Last message */}
                  <p className="text-xs text-muted-foreground truncate">{lead.last_message || '—'}</p>

                  {/* Stage */}
                  <div>
                    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full', `${STAGE_CONFIG[lead.pipeline_stage]?.color}/15`, STAGE_CONFIG[lead.pipeline_stage]?.text)}>
                      <span className={cn('w-1.5 h-1.5 rounded-full', STAGE_CONFIG[lead.pipeline_stage]?.color)} />
                      {STAGE_CONFIG[lead.pipeline_stage]?.label || lead.pipeline_stage}
                    </span>
                  </div>

                  {/* Score */}
                  <ScoreBadge score={lead.lead_score} />

                  {/* Last active */}
                  <span className="text-xs text-muted-foreground">{formatTime(lead.last_message_at)}</span>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`lead-actions-${lead.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-card border-border">
                      <DropdownMenuItem onClick={() => { setEditingLead(lead); setDialogOpen(true); }}><Edit className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleRescore(lead.id)}><TrendingUp className="w-3.5 h-3.5 mr-2" />Rescore</DropdownMenuItem>
                      {lead.conversation_id && <DropdownMenuItem onClick={() => navigate(`/conversations/${lead.conversation_id}`)}><Eye className="w-3.5 h-3.5 mr-2" />View Chat</DropdownMenuItem>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-400" onClick={() => handleDelete(lead.id)}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </Card>

      <LeadFormDialog open={dialogOpen} onOpenChange={setDialogOpen} lead={editingLead} onSave={handleSaveLead} />
    </div>
  );
};
